import React, { useState, useCallback, useRef, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { membershipOffersQuery } from "components/GoodsamMembership/query.js";
import { getErrorsAndSuccess } from "utils/extractErrors";
import GoodsamMembership from "components/GoodsamMembership";
import GoodsamMembershipPlanSelection from "components/GoodsamMembership/GoodsamMembershipPlanSelection";
import { SpinnerBack } from "components/Spinner/Spinner";
import { steps as stepsList } from "./steps";
import {
  setSelectedToStep,
  setCompletedToStep,
  setSelectedAndWasSelectedToStep,
  isLastStep
} from "components/StepsMenu/utils";
import { useGoodsamMembershipModalSegment } from "./hooks/useGoodsamMembershipModalSegment";
import NotificationPopover from "components/NotificationPopover/NotificationPopover";

const goodSamMembershipOffersEAS = getErrorsAndSuccess("good_sam_membership_offers");

const BecomeMemberShip = ({
  show,
  clubId,
  onCloseBecomeMemberShip = () => {},
  handleActionAfterLastStep,
  setIsShowMembershipModal,
  onMembershipValidationSuccess
}) => {
  const [isDeclined, setIsDeclined] = useState(false);
  const [steps, changeSteps] = useState(stepsList || []);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [planId, setPlanId] = useState(null);
  const [planPrice, setPlanPrice] = useState(null);
  const CurrentStepComponent =
    steps[currentStepIndex]?.component || GoodsamMembershipPlanSelection;
  const isSegmentLoaded = useRef(false);
  const [isMembershipValidationSuccess, setIsMembershipValidationSuccess] = useState(false);

  const { loading: membershipOffersLoading, refetch: refetchMembershipOffers, data: membershipOffersData } = useQuery(membershipOffersQuery, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true
  });
  const stepId = steps?.[currentStepIndex]?.id || null;

  const {
    trackSegmentGSModalLoad,
    trackSegmentPlanSelectionLoad,
    trackSegmentNextStep,
    trackSegmentOrderCompleted,
    trackSegmentStep
  } = useGoodsamMembershipModalSegment({ gsmOffers: membershipOffersData?.good_sam_membership_offers, membershipId: planId, priceTotal: planPrice?.priceTotal });

  const onChangeStep = useCallback(
    () => {
      changeSteps(setCompletedToStep(setSelectedAndWasSelectedToStep(steps, currentStepIndex + 1), currentStepIndex));
      if (!isLastStep(steps, currentStepIndex)) {
        setCurrentStepIndex(currentStepIndex + 1);
      } else {
        trackSegmentOrderCompleted(planId);
        onCloseBecomeMemberShip();
        handleActionAfterLastStep?.();
      }
    },
    [currentStepIndex, handleActionAfterLastStep, onCloseBecomeMemberShip, planId, steps, trackSegmentOrderCompleted]
  );

  const onRefetchMembershipOffers = useCallback(
    async () => {
      const response = await refetchMembershipOffers();
      const goodSamMembershipOffers = goodSamMembershipOffersEAS.getQueryData(response);

      if (goodSamMembershipOffers) {
        const { price, price_total, tax } = goodSamMembershipOffers.find(({ id }) => planId === id);

        setPlanPrice({
          price: price,
          priceTotal: price_total,
          taxes: tax
        });
      }
    },
    [planId, refetchMembershipOffers]
  );

  const goNextStep = useCallback(async ({ idStep = null } = {}) => {
    trackSegmentNextStep(currentStepIndex + 1, planId);
    if (idStep === "payment-method") {
      await onRefetchMembershipOffers();
    } else {
      onChangeStep();
    }
  }, [planId, trackSegmentNextStep, currentStepIndex, onRefetchMembershipOffers, onChangeStep]);

  const onSavePlan = useCallback(
    (id, price) => {
      trackSegmentNextStep(currentStepIndex + 1, id);
      setPlanId(id);
      setPlanPrice(price);
      onChangeStep();
    },
    [currentStepIndex, onChangeStep, trackSegmentNextStep]
  );

  const onClickStep = useCallback(({ steps, selectedStepIndex }) => {
    trackSegmentStep(selectedStepIndex, planId);
    changeSteps(setSelectedToStep(steps, selectedStepIndex));
    setCurrentStepIndex(selectedStepIndex);
  }, [planId, trackSegmentStep]);


  const handleOnMembershipValidationSuccess = useCallback(
    () => {
      onMembershipValidationSuccess();
      setIsMembershipValidationSuccess(true);
    },
    [onMembershipValidationSuccess, setIsMembershipValidationSuccess]
  );

  useEffect(() => {
    if (show) {
      trackSegmentGSModalLoad();
      trackSegmentPlanSelectionLoad();
    }
  }, [trackSegmentGSModalLoad, show, trackSegmentPlanSelectionLoad]);

  useEffect(() => {
    if (membershipOffersData?.good_sam_membership_offers?.[0]?.id && !planId) {
      setPlanId(membershipOffersData?.good_sam_membership_offers?.[0].id);
    }
  }, [membershipOffersData?.good_sam_membership_offers, planId]);

  return (
    <>
      {membershipOffersLoading && <SpinnerBack />}
      <GoodsamMembership
        show={show}
        onClose={() => {
          onCloseBecomeMemberShip();
          setTimeout(() => setCurrentStepIndex(0), 500);
        }}
        onMembershipValidationSuccess={handleOnMembershipValidationSuccess}
        setIsShowMembershipModal={setIsShowMembershipModal}
        setIsGoodSamMember={() => {}}
        setIsDeclined={setIsDeclined}
        onSavePlan={onSavePlan}
        activeId="1"
        onClickStep={onClickStep}
        onNextStep={goNextStep}
        steps={steps}
        stepId={stepId}
        currentStepIndex={currentStepIndex}
        CurrentStepComponent={CurrentStepComponent}
        planPrice={planPrice}
        planId={planId}
        clubId={clubId}
      />

      {isMembershipValidationSuccess && (
        <NotificationPopover
          show={true}
          status="success"
          text="You successfully validated your membership details"
          onClose={() => {
            setIsMembershipValidationSuccess(false);
          }}
        />
      )}
    </>
  );
};

BecomeMemberShip.propTypes = {};

export default BecomeMemberShip;
